import "@automatiq/seating-charts-wc";

/** @type {HTMLElement | null} */
let aqChartEl = null;
/** Parsed coordinates payload from /api/seat-map */
let aqCoordinates = null;
/** Flat list of { id, name } from coordinates */
let aqSectionFlat = [];
const allCoordinateSectionIds = () => aqSectionFlat.map((s) => s.id);

function normalizeKey(value) {
    return (value || "").trim().toLowerCase();
}

function buildSectionFlatList(coordinates) {
    const zones = coordinates?.zones || [];
    const list = [];
    for (const zone of zones) {
        for (const section of zone.sections || []) {
            list.push({
                id: section.id,
                name: (section.name || "").trim()
            });
        }
    }
    return list;
}

function resolveSectionIdForTicket(ticket, sectionFlat) {

    if (ticket === null) {
        console.error("Ticket is empty");
        return null;
    }

    const idKey = normalizeKey(ticket.venueSectionId);
    const nameKey = normalizeKey(ticket.venueSectionName);
    const sectionKey = normalizeKey(ticket.section);

    for (const s of sectionFlat) {
        if (idKey && normalizeKey(s.id) === idKey) {
            return s.id;
        } else if (nameKey && normalizeKey(s.name) === nameKey) {
            return s.id;
        } else if (sectionKey && (normalizeKey(s.id) === sectionKey || normalizeKey(s.name) === sectionKey)) {
            return s.id;
        }
    }
    return null;
}

function buildInventorySectionIds(sectionFlat, tickets) {
    const ids = new Set();
    for (const t of tickets || []) {
        const id = resolveSectionIdForTicket(t, sectionFlat);
        if (id) ids.add(id);
    }
    return ids;
}

/**
 * @param {Set<string>} inventoryIds
 * @param {Set<string>} activeIds  sections user selected on map (status ACTIVE)
 */
function buildSectionsState(inventoryIds, activeIds) {
    const out = {};
    for (const id of allCoordinateSectionIds()) {
        if (!inventoryIds.has(id)) {
            out[id] = { status: "DISABLED" };
        } else {
            out[id] = { status: activeIds.has(id) ? "ACTIVE" : "INACTIVE" };
        }
    }
    return out;
}

function getActiveSectionIdsFromState(sectionsState) {
    return Object.entries(sectionsState || {})
        .filter(([, v]) => v && v.status === "ACTIVE")
        .map(([id]) => id);
}

/** Current map sections object (mirrors WC state after our updates). */
let aqSectionsState = {};

function notifySelectionChange() {
    const activeIds = getActiveSectionIdsFromState(aqSectionsState);
    if (typeof window.__aqOnMapSectionSelectionChange === "function") {
        window.__aqOnMapSectionSelectionChange(activeIds);
    }
}

/**
 * @param {unknown[]} filteredTickets Tickets that passed list filters (before map section filter) — same idea as TEVO updateTicketGroups.
 */
window.updateAqSeatmapInventoryFromTickets = function (filteredTickets) {
    if (!aqChartEl || !aqCoordinates || !aqSectionFlat.length) return;

    window.__aqLastInventoryTickets = filteredTickets ? [...filteredTickets] : [];

    const inventoryIds = buildInventorySectionIds(aqSectionFlat, filteredTickets);
    const prevActive = new Set(getActiveSectionIdsFromState(aqSectionsState));
    const nextActive = new Set(
        [...prevActive].filter((id) => inventoryIds.has(id))
    );

    aqSectionsState = buildSectionsState(inventoryIds, nextActive);
    if (typeof aqChartEl.updateSections === "function") {
        aqChartEl.updateSections(aqSectionsState);
    }

    notifySelectionChange();
};

window.__aqTicketToSectionId = function (ticket) {
    return resolveSectionIdForTicket(ticket, aqSectionFlat);
};

window.clearAqSeatmapSectionSelection = function () {
    if (!aqChartEl || !aqSectionFlat.length) return;

    const currentTickets = window.__aqLastInventoryTickets;
    const inventoryIds = buildInventorySectionIds(
        aqSectionFlat,
        Array.isArray(currentTickets) ? currentTickets : []
    );

    aqSectionsState = buildSectionsState(inventoryIds, new Set());

    if (typeof aqChartEl.updateSections === "function") {
        aqChartEl.updateSections(aqSectionsState);
    }

    notifySelectionChange();
};

/**
 * @param {string} venueId
 * @param {string | null | undefined} configurationId
 * @param {unknown[]} [ticketsForInventory] ticket groups used to enable only sections with inventory
 */
window.loadAqSeatMap = async function (venueId, configurationId, ticketsForInventory) {
    const wrapper = document.getElementById("aq-seatmap-container");
    if (!wrapper) return;

    const unavailableHtml =
        '<p class="text-muted text-center mb-0" style="padding:16px;">No seat map available</p>';

    try {
        if (!venueId) {
            wrapper.innerHTML = unavailableHtml;
            return;
        }

        wrapper.innerHTML = `<p style="padding:16px;">Loading seating chart...</p>`;

        const params = new URLSearchParams({ venueId });

        if (configurationId) {
            params.set("configurationId", configurationId);
        }

        const response = await fetch(`/api/seat-map?${params.toString()}`);
        let data = null;
        try {
            data = await response.json();
        } catch {
            throw new Error("Seat map response was not valid JSON");
        }

        if (!response.ok) {
            // AQ often has no map for a venue/config — never surface API error text in the UI.
            throw new Error(data?.error || `Seat map request failed: ${response.status}`);
        }

        await customElements.whenDefined("seating-chart");

        const chart = document.createElement("seating-chart");

        chart.style.display = "block";
        chart.style.width = "100%";
        chart.style.height = "100%";
        chart.style.boxSizing = "border-box";

        aqCoordinates = data.coordinates;
        aqSectionFlat = buildSectionFlatList(aqCoordinates);
        window.__aqLastInventoryTickets = Array.isArray(ticketsForInventory)
            ? ticketsForInventory
            : [];

        const inventoryIds = buildInventorySectionIds(
            aqSectionFlat,
            window.__aqLastInventoryTickets
        );
        const emptyActive = new Set();
        aqSectionsState = buildSectionsState(inventoryIds, emptyActive);

        const highlightSections = Object.fromEntries(
            allCoordinateSectionIds().map((sectionId) => [sectionId, false])
        );

        // Zoom is d3-based inside a fixed-size SVG; panned/zoomed artwork often does not cover the
        // full viewport, so the leftover area shows as white. There is no documented controlOptions
        // key to auto-fit the viewport to the zoom transform (would need Automatiq support).
        const controlOptions = {
            disableMultiSelect: false,
            disableSelection: false,
            showRows: false,
            zoomable: true
        };

        setStringPropAndAttr(chart, "background", data.background);
        setObjectPropAndAttr(chart, "coordinates", data.coordinates);
        setObjectPropAndAttr(chart, "controlOptions", controlOptions);
        setObjectPropAndAttr(chart, "control", controlOptions);
        setObjectPropAndAttr(chart, "highlightSections", highlightSections);
        setObjectPropAndAttr(chart, "sections", aqSectionsState);
        setObjectPropAndAttr(chart, "rows", {});
        setObjectPropAndAttr(chart, "unmappedSectionPaths", []);
        setStringPropAndAttr(chart, "source", "B2B");

        chart.mouseEnterHandler = function () {};

        chart.mouseLeaveHandler = function () {};

        /**
         * WC passes (payload, setHandleSections). Must call setHandleSections to persist toggle — see @automatiq/seating-charts-wc.
         */
        chart.selectHandler = function (payload, setHandleSections) {
            if (!payload || payload.type !== "section") {
                return;
            }

            setHandleSections((prev) => {
                const next = { ...(prev || {}) };
                next[payload.id] = { status: payload.status };
                aqSectionsState = next;

                const activeIds = getActiveSectionIdsFromState(next);
                if (typeof window.__aqOnMapSectionSelectionChange === "function") {
                    window.__aqOnMapSectionSelectionChange(activeIds);
                }

                return next;
            });
        };

        wrapper.innerHTML = "";
        wrapper.appendChild(chart);
        aqChartEl = chart;

        fixAqSeatMapSvg(chart);

        function fixAqSeatMapSvg(chart) {
            const apply = () => {
                const svg = chart.shadowRoot?.querySelector("svg");
                if (!svg) return false;

                svg.style.width = "100%";
                svg.style.height = "100%";
                svg.style.maxWidth = "100%";
                svg.style.maxHeight = "100%";
                svg.setAttribute("preserveAspectRatio", "xMidYMid meet");

                return true;
            };

            if (apply()) return;

            const observer = new MutationObserver(() => {
                if (apply()) {
                    observer.disconnect();
                }
            });

            observer.observe(chart.shadowRoot || chart, {
                childList: true,
                subtree: true
            });
        }


    } catch (error) {
        console.error("Seat map error:", error);
        wrapper.innerHTML = unavailableHtml;
    }
};

function setObjectPropAndAttr(element, name, value) {
    element[name] = value;
    element.setAttribute(name, JSON.stringify(value));
}

function setStringPropAndAttr(element, name, value) {
    element[name] = value || "";
    element.setAttribute(name, value || "");
}
